import { jsx, jsxs } from "react/jsx-runtime";
import { Button, DatePicker, PlainButton, TRANSFORMER_MAX_MOBILE_WIDTH_PX, TravelerSelection, cn } from "@pcln/horizon";
import { addMonths } from "date-fns/addMonths";
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useMediaQuery } from "react-responsive";
import { FormAutocompleteField, resolveAutocompleteFieldOptions } from "../shared/components/Autocomplete/FormAutocompleteField.js";
import { BundleAndSave } from "../shared/components/BundleAndSave.js";
import { ItineraryBar } from "../shared/components/ItineraryBar.js";
import { SearchFormGrid } from "../shared/components/SearchFormGrid.js";
import { SubmitButton } from "../shared/components/SubmitButton.js";
import { DEFAULT_COLLISION_AVOIDANCE, ITINERARY_BAR_BREAKPOINT_PX } from "../shared/constants.js";
import { useContainerBreakpoint } from "../shared/hooks/useContainerBreakpoint.js";
import { useFormChangedFromInitial } from "../shared/hooks/useFormChangedFromInitial.js";
import { useTabGroup } from "../shared/hooks/useTabGroup.js";
import { formatItineraryDateRange, formatItineraryLocationTitle, formatItineraryTravelers } from "../shared/utils/itinerary-bar.js";
import { createYupResolver } from "../shared/validation/yup-form-resolver.js";
import { HOTEL_LABELS, LISTINGS_DATE_PICKER_CUSTOM_FORMAT } from "./constants.js";
import { DEFAULT_HOTEL_FORM_STATE, STAY_SEARCH_CALENDAR_MONTHS, getHotelTravelerFieldErrorMessage, useHotelDateRangeCalendar } from "./hotelFormShared.js";
import { getBackButtonClass, getFormContainerClass, getGridContainerClass, getInputClass, getItineraryBarContainerClass, getWideLayoutBreakpointPx } from "./HotelSearchForm.variants.js";
import { HOTEL_MAX_CHILDREN, HOTEL_MAX_ROOMS, HOTEL_MAX_TRAVELERS } from "./hotelTravelerLimits.js";
import { PriceFilterControl } from "./PriceFilterControl.js";
import validation, { INFANT_CAPACITY_LIMIT, isInfantLimitReachedPackages } from "./validation.js";
function HotelSearchForm({ onSubmit, initialValues, isMobile = false, renderAutocomplete, autocompleteOptions, onLocationSelect, apiRef, setLocation, showBundleAndSave = true, removeBackground = false, highlightBundleAndSave = false, onBundleChange, onFormValuesChange, onItemClick, autoAdvance = false, typeAheadEndpoint = 'hotels', hotelPlannerLinkHref, onMaxRoomsClick, renderSelectedFilterChipBar, isStickyInEffect = false, collisionAvoidance = DEFAULT_COLLISION_AVOIDANCE, disabledDates, submitLabel = 'Find Your Hotel', submitButtonType = 'primary', disableSubmitButton = false, submitButtonEmphasis, submitButtonFixedOnMobile, singleRow = false, autoOpenCalendar = false, showCurrentLocation = true, toolbarProps, showPetFriendlyToggle = false, onAddAnotherHotel, itineraryBar = false, showPriceFilter = false, priceFilterProps }) {
    const { onBackButtonClick, backButtonLabel = HOTEL_LABELS.backButton } = toolbarProps ?? {};
    const shouldFixSubmitButtonOnMobile = submitButtonFixedOnMobile ?? isMobile;
    const mergedInitialValues = useMemo(()=>({
            ...DEFAULT_HOTEL_FORM_STATE,
            ...initialValues
        }), [
        initialValues
    ]);
    const [appliedValues, setAppliedValues] = useState({
        datesVersion: 0,
        travelersVersion: 0
    });
    const seededValues = useMemo(()=>({
            ...mergedInitialValues,
            ...void 0 !== appliedValues.startDate && {
                startDate: appliedValues.startDate
            },
            ...void 0 !== appliedValues.endDate && {
                endDate: appliedValues.endDate
            },
            ...void 0 !== appliedValues.travelers && {
                travelers: appliedValues.travelers
            }
        }), [
        mergedInitialValues,
        appliedValues
    ]);
    const { registerTabElement, focusNextElement } = useTabGroup();
    const [addFlights, setAddFlights] = useState(false);
    const [searchOriginResetGeneration, setSearchOriginResetGeneration] = useState(0);
    const finalizeFeedbackRef = useRef(null);
    const registerFinalizeFeedback = useCallback((flush)=>{
        finalizeFeedbackRef.current = flush;
    }, []);
    const isMobileComponent = useMediaQuery({
        maxWidth: TRANSFORMER_MAX_MOBILE_WIDTH_PX
    });
    const [isAtOrAboveItineraryBarBreakpoint, itineraryBarContainerRef] = useContainerBreakpoint(ITINERARY_BAR_BREAKPOINT_PX, !isMobile);
    const showItineraryBar = itineraryBar && !isAtOrAboveItineraryBarBreakpoint;
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    useEffect(()=>{
        if (!showItineraryBar) setIsDrawerOpen(false);
    }, [
        showItineraryBar
    ]);
    const { clearErrors, control, getValues, setValue, handleSubmit, watch, trigger, formState: { errors } } = useForm({
        defaultValues: mergedInitialValues,
        mode: 'onSubmit',
        resolver: createYupResolver(validation),
        reValidateMode: 'onChange'
    });
    const hasFormChanged = useFormChangedFromInitial(control);
    const watchTripType = useWatch({
        control,
        name: 'tripType',
        defaultValue: mergedInitialValues.tripType
    });
    const watchStartLocation = useWatch({
        control,
        name: 'startLocation',
        defaultValue: mergedInitialValues.startLocation
    });
    const watchEndLocation = useWatch({
        control,
        name: 'endLocation',
        defaultValue: mergedInitialValues.endLocation
    });
    const watchStartDate = useWatch({
        control,
        name: 'startDate',
        defaultValue: mergedInitialValues.startDate
    });
    const watchEndDate = useWatch({
        control,
        name: 'endDate',
        defaultValue: mergedInitialValues.endDate
    });
    const watchTravelers = useWatch({
        control,
        name: 'travelers',
        defaultValue: mergedInitialValues.travelers
    });
    const watchPetFriendly = useWatch({
        control,
        name: 'petFriendly',
        defaultValue: mergedInitialValues.petFriendly ?? false
    });
    useEffect(()=>{
        const subscription = watch((value)=>{
            if (onFormValuesChange) onFormValuesChange(value);
        });
        return ()=>subscription.unsubscribe();
    }, [
        watch,
        onFormValuesChange
    ]);
    useEffect(()=>{
        if (setLocation && watchEndLocation) setLocation(watchEndLocation);
    }, [
        watchEndLocation,
        setLocation
    ]);
    useEffect(()=>{
        clearErrors('startLocation');
    }, [
        clearErrors,
        watchStartLocation
    ]);
    useEffect(()=>{
        clearErrors('endLocation');
    }, [
        clearErrors,
        watchEndLocation
    ]);
    useEffect(()=>{
        clearErrors('startDate');
    }, [
        clearErrors,
        watchStartDate,
        watchTripType
    ]);
    useEffect(()=>{
        clearErrors('endDate');
    }, [
        clearErrors,
        watchEndDate,
        watchTripType
    ]);
    useEffect(()=>{
        if (watchTravelers) {
            trigger('travelers.children');
            trigger('travelers.rooms');
        }
    }, [
        trigger,
        watchTripType,
        watchTravelers
    ]);
    const [bundleSelections, setBundleSelections] = useState({
        flights: false,
        car: false
    });
    const handleBundleSelectionChange = useCallback((updatedTripType, checked, optionValue)=>{
        setBundleSelections((prev)=>({
                ...prev,
                [optionValue]: checked
            }));
        setValue('tripType', updatedTripType);
        onBundleChange?.(updatedTripType, checked, optionValue);
        if ('FLY' === optionValue) setAddFlights(checked);
    }, [
        setValue,
        onBundleChange
    ]);
    const { currentDate, defaultMonth, initialSelectedDates, maxSelectableDate, onCalendarChange, clearMaxSelectableDate } = useHotelDateRangeCalendar({
        setValue,
        startDate: seededValues.startDate,
        endDate: seededValues.endDate
    });
    const [selectedFilterChipData, setSelectedFilterChipData] = useState(()=>({
            amenities: new Set(mergedInitialValues.selectedTopFilters?.amenities),
            hotelStarLevel: new Set(mergedInitialValues.selectedTopFilters?.hotelStarLevel),
            guestRating: new Set(mergedInitialValues.selectedTopFilters?.guestRating),
            propertyTypes: new Set(mergedInitialValues.selectedTopFilters?.propertyTypes)
        }));
    const [geolocationError, setGeolocationError] = useState(null);
    const onItemSelect = useCallback((item, searchKey, context)=>{
        setValue(searchKey, item);
        if ('endLocation' === searchKey && item) setGeolocationError(null);
        onLocationSelect?.(searchKey, item, context);
        if (context?.isPrefill) return;
        if (isMobile || !item) {
            if (isMobile && !!item && document.activeElement instanceof HTMLElement) document.activeElement.blur();
        } else {
            onItemClick?.();
            if (autoAdvance) focusNextElement(true);
        }
    }, [
        isMobile,
        setValue,
        onItemClick,
        autoAdvance,
        focusNextElement,
        onLocationSelect
    ]);
    useImperativeHandle(apiRef, ()=>({
            setLocationValue: (searchKey, item)=>{
                onItemSelect(item, searchKey);
            },
            setSearchValues: ({ startDate, endDate, travelers })=>{
                if (void 0 !== startDate) setValue('startDate', startDate);
                if (void 0 !== endDate) setValue('endDate', endDate);
                if (void 0 !== travelers) setValue('travelers', travelers);
                const hasDates = void 0 !== startDate || void 0 !== endDate;
                if (hasDates) clearMaxSelectableDate();
                setAppliedValues((previous)=>({
                        ...previous,
                        ...hasDates && {
                            startDate: startDate ?? getValues('startDate'),
                            endDate: endDate ?? getValues('endDate')
                        },
                        ...void 0 !== travelers && {
                            travelers
                        },
                        ...hasDates && {
                            datesVersion: previous.datesVersion + 1
                        },
                        ...void 0 !== travelers && {
                            travelersVersion: previous.travelersVersion + 1
                        }
                    }));
            }
        }), [
        onItemSelect,
        getValues,
        setValue,
        clearMaxSelectableDate
    ]);
    const startLocationAutocompleteOptions = resolveAutocompleteFieldOptions(autocompleteOptions, {
        searchKey: 'startLocation',
        typeAheadEndpoint: 'packagesOrigin'
    });
    const endLocationAutocompleteOptions = resolveAutocompleteFieldOptions(autocompleteOptions, {
        searchKey: 'endLocation',
        typeAheadEndpoint
    });
    const hasSelectedFilters = Object.values(selectedFilterChipData).some((value)=>value.size > 0);
    const showSelectedFilters = 'STAY' === watchTripType && hasSelectedFilters;
    const handleTravelerChange = (travelerInfo)=>{
        setValue('travelers', {
            adults: travelerInfo.adults,
            children: travelerInfo.children,
            lapInfants: travelerInfo.lapInfants,
            childrenAges: travelerInfo.childrenAges,
            rooms: travelerInfo.rooms
        });
    };
    const [appliedPrice, setAppliedPrice] = useState(null);
    const priceFilterCityId = useMemo(()=>(watchEndLocation?.type === 'CITY' ? watchEndLocation.id || watchEndLocation.cityID : watchEndLocation?.cityID) || void 0, [
        watchEndLocation
    ]);
    const didMountPriceFilterRef = useRef(false);
    useEffect(()=>{
        if (!didMountPriceFilterRef.current) {
            didMountPriceFilterRef.current = true;
            return;
        }
        setAppliedPrice(null);
    }, [
        priceFilterCityId,
        watchStartDate,
        watchEndDate,
        priceFilterProps?.currencySymbol,
        priceFilterProps?.ceiling,
        priceFilterProps?.countryCode
    ]);
    const handleSubmitHelper = handleSubmit((data)=>{
        finalizeFeedbackRef.current?.();
        setSearchOriginResetGeneration((generation)=>generation + 1);
        setIsDrawerOpen(false);
        onSubmit({
            ...data,
            mapView: false,
            selectedTopFilters: selectedFilterChipData,
            ...showPriceFilter && appliedPrice ? appliedPrice : {}
        });
    });
    const numberOfMonthsDisplay = 'STAY' === watchTripType ? STAY_SEARCH_CALENDAR_MONTHS : 12;
    const formIsStickyInEffect = isStickyInEffect && !showItineraryBar;
    const hasBackButton = !!onBackButtonClick && !showItineraryBar;
    const hasAddAnotherHotel = !!onAddAnotherHotel && !formIsStickyInEffect;
    const hasPriceFilter = showPriceFilter && !formIsStickyInEffect;
    const formClasses = getFormContainerClass(addFlights, formIsStickyInEffect, showSelectedFilters);
    const itineraryTitle = useMemo(()=>addFlights ? formatItineraryLocationTitle(watchStartLocation, watchEndLocation) : formatItineraryLocationTitle(watchEndLocation), [
        addFlights,
        watchStartLocation,
        watchEndLocation
    ]);
    const itineraryData1 = useMemo(()=>formatItineraryDateRange(watchStartDate, watchEndDate), [
        watchStartDate,
        watchEndDate
    ]);
    const itineraryData2 = useMemo(()=>formatItineraryTravelers(watchTravelers, {
            roomsEnabled: true
        }), [
        watchTravelers
    ]);
    const [isWideLayout, containerRef] = useContainerBreakpoint(getWideLayoutBreakpointPx(addFlights, formIsStickyInEffect, showSelectedFilters, singleRow), !isMobile, isDrawerOpen);
    const gridContainerClass = getGridContainerClass(addFlights, formIsStickyInEffect, showSelectedFilters, singleRow, hasBackButton, hasAddAnotherHotel || hasPriceFilter, showBundleAndSave);
    const trailingControlsSection = hasAddAnotherHotel || hasPriceFilter ? /*#__PURE__*/ jsxs("div", {
        className: cn(getInputClass('addHotel'), 'flex flex-col items-start gap-3'),
        children: [
            hasAddAnotherHotel && /*#__PURE__*/ jsx(PlainButton, {
                type: "primary",
                textStyle: "body3",
                emphasis: "medium",
                iconLeft: "add",
                buttonType: "button",
                onClick: onAddAnotherHotel,
                children: "Add another hotel"
            }),
            hasPriceFilter && /*#__PURE__*/ jsx(PriceFilterControl, {
                cityId: priceFilterCityId,
                checkIn: watchStartDate,
                checkOut: watchEndDate,
                isMobile: isMobile,
                ...priceFilterProps,
                applied: appliedPrice,
                onApply: setAppliedPrice
            })
        ]
    }) : null;
    const packageDealsSection = showBundleAndSave ? /*#__PURE__*/ jsx("div", {
        className: getInputClass('packageDeals', formIsStickyInEffect),
        children: /*#__PURE__*/ jsx(BundleAndSave, {
            checkedValues: bundleSelections,
            onSelectionChange: handleBundleSelectionChange,
            tripType: watchTripType,
            productType: "hotels",
            removeBackground: removeBackground,
            highlightBackground: highlightBundleAndSave
        })
    }) : null;
    const formGrid = /*#__PURE__*/ jsxs(SearchFormGrid, {
        gridClassName: gridContainerClass,
        ariaLabel: "horizon-search-form",
        onSubmit: handleSubmitHelper,
        containerRef: containerRef,
        containerClassName: formClasses,
        formClassName: formIsStickyInEffect ? 'w-full min-w-0' : void 0,
        innerClassName: formIsStickyInEffect ? 'w-full min-w-0' : void 0,
        children: [
            hasBackButton && /*#__PURE__*/ jsx(Button, {
                buttonType: "button",
                type: "tertiary",
                className: getBackButtonClass(addFlights, formIsStickyInEffect, showSelectedFilters, singleRow),
                iconLeft: "chevron_left",
                onClick: onBackButtonClick,
                children: backButtonLabel
            }),
            addFlights && /*#__PURE__*/ jsx("div", {
                className: getInputClass('flight'),
                children: /*#__PURE__*/ jsx(FormAutocompleteField, {
                    ...startLocationAutocompleteOptions,
                    renderAutocomplete: renderAutocomplete,
                    showFilterChips: false,
                    searchKey: "startLocation",
                    label: HOTEL_LABELS.startLocation,
                    typeAheadEndpoint: "packagesOrigin",
                    errorMessage: errors.startLocation?.message,
                    initialSelectedItem: watchStartLocation,
                    onItemSelect: (item, context)=>onItemSelect(item, 'startLocation', context),
                    inputRef: autoAdvance ? registerTabElement : void 0,
                    collisionAvoidance: collisionAvoidance
                })
            }),
            /*#__PURE__*/ jsx("div", {
                className: getInputClass('location'),
                children: /*#__PURE__*/ jsx(FormAutocompleteField, {
                    ...endLocationAutocompleteOptions,
                    renderAutocomplete: renderAutocomplete,
                    searchKey: "endLocation",
                    label: HOTEL_LABELS.endLocation,
                    typeAheadEndpoint: typeAheadEndpoint,
                    errorMessage: errors.endLocation?.message ?? geolocationError ?? void 0,
                    initialSelectedItem: watchEndLocation,
                    onItemSelect: (item, context)=>onItemSelect(item, 'endLocation', context),
                    inputRef: autoAdvance ? registerTabElement : void 0,
                    collisionAvoidance: collisionAvoidance,
                    showCurrentLocation: showCurrentLocation,
                    onGeolocationError: setGeolocationError,
                    selectedFilterChipData: selectedFilterChipData,
                    setSelectedFilterChipData: setSelectedFilterChipData,
                    searchOriginResetGeneration: searchOriginResetGeneration,
                    onRegisterFinalizeFeedback: registerFinalizeFeedback,
                    showFilterChips: (endLocationAutocompleteOptions.showFilterChips ?? true) && 'STAY' === watchTripType
                })
            }),
            /*#__PURE__*/ jsx("div", {
                className: getInputClass('datePicker'),
                children: /*#__PURE__*/ jsx(DatePicker, {
                    errorMessage: errors.startDate?.message || errors.endDate?.message,
                    className: "w-full",
                    title: "Hotels: Check-in - Check-out",
                    label: HOTEL_LABELS.datePicker,
                    isOpen: autoOpenCalendar,
                    mode: "range",
                    numberOfMonths: isMobileComponent ? numberOfMonthsDisplay : 2,
                    displayNights: true,
                    handleCalendarChange: onCalendarChange,
                    initialSelected: initialSelectedDates,
                    disabled: {
                        before: disabledDates?.before || currentDate,
                        after: disabledDates?.after || maxSelectableDate
                    },
                    startMonth: disabledDates?.before || currentDate,
                    endMonth: addMonths(currentDate, numberOfMonthsDisplay - 1),
                    defaultMonth: defaultMonth,
                    min: 1,
                    inputRef: autoAdvance ? registerTabElement : void 0,
                    collisionAvoidance: collisionAvoidance,
                    customDateFormat: singleRow ? LISTINGS_DATE_PICKER_CUSTOM_FORMAT : void 0
                }, `date-picker-${appliedValues.datesVersion}`)
            }),
            /*#__PURE__*/ jsx("div", {
                className: getInputClass('travelers'),
                children: /*#__PURE__*/ jsx(TravelerSelection, {
                    isHotel: !watchTripType.includes('FLY'),
                    initialValues: seededValues.travelers,
                    roomsEnabled: true,
                    maxRooms: 'STAY' === watchTripType ? HOTEL_MAX_ROOMS : 4,
                    maxChildren: 'STAY' === watchTripType ? HOTEL_MAX_CHILDREN : void 0,
                    maxTravelers: 'STAY' === watchTripType ? HOTEL_MAX_TRAVELERS : 8,
                    fireMaxRoomsOnClick: onMaxRoomsClick,
                    ageSelectEnabled: true,
                    tooltipErrorMessageId: "travelerSelectionError",
                    errorMessage: getHotelTravelerFieldErrorMessage(errors.travelers) || isInfantLimitReachedPackages(watchTravelers?.adults, watchTravelers?.childrenAges, watchTripType) && INFANT_CAPACITY_LIMIT || '',
                    onChange: (values)=>handleTravelerChange(values),
                    collisionAvoidance: collisionAvoidance,
                    showPetFriendlyToggle: showPetFriendlyToggle,
                    isPetFriendly: !!watchPetFriendly,
                    onPetFriendlyChange: (checked)=>setValue('petFriendly', checked),
                    ...hotelPlannerLinkHref && {
                        maxRoomsLinkAttrs: {
                            href: hotelPlannerLinkHref,
                            target: '_blank'
                        }
                    }
                }, `traveler-selection-${appliedValues.travelersVersion}`)
            }),
            showSelectedFilters && renderSelectedFilterChipBar && /*#__PURE__*/ jsx("div", {
                className: getInputClass('selectedFilters'),
                children: renderSelectedFilterChipBar({
                    selectedFilterChipData,
                    setSelectedFilterChipData
                })
            }),
            !isWideLayout && trailingControlsSection,
            !isWideLayout && packageDealsSection,
            /*#__PURE__*/ jsx(SubmitButton, {
                className: getInputClass('submit'),
                isMobile: isMobile && shouldFixSubmitButtonOnMobile && !showItineraryBar,
                type: submitButtonType,
                emphasis: submitButtonEmphasis,
                disabled: disableSubmitButton && !hasFormChanged,
                children: submitLabel
            }),
            isWideLayout && trailingControlsSection,
            isWideLayout && packageDealsSection
        ]
    });
    return /*#__PURE__*/ jsx("div", {
        ref: itineraryBarContainerRef,
        className: getItineraryBarContainerClass(isStickyInEffect),
        children: showItineraryBar ? /*#__PURE__*/ jsx(ItineraryBar, {
            title: itineraryTitle,
            data1: itineraryData1,
            data2: itineraryData2,
            drawerTitle: "Edit search",
            buttonRight1: toolbarProps?.buttonRight1,
            buttonRight2: toolbarProps?.buttonRight2,
            onBackButtonClick: onBackButtonClick,
            backButtonLabel: backButtonLabel,
            open: isDrawerOpen,
            onOpenChange: setIsDrawerOpen,
            children: formGrid
        }) : formGrid
    });
}
export { HotelSearchForm };
